<script setup>
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';

defineProps({
  agents: { type: Array, default: () => [] },
  caseData: { type: Object, required: true },
  responsibleUserId: { type: [String, Number], default: '' },
});

const emit = defineEmits(['assign', 'update:responsibleUserId']);
const { t } = useI18n();

const formatDate = value => {
  if (!value) return t('POST_SALE.COMMON.NO_DATE');
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'medium' }).format(
    new Date(value)
  );
};

const priorityClass = priority =>
  ({
    critica: 'bg-red-100 text-red-700',
    alta: 'bg-orange-100 text-orange-700',
    normal: 'bg-blue-100 text-blue-700',
    baixa: 'bg-slate-100 text-slate-600',
  })[priority] || 'bg-slate-100 text-slate-600';

const priorityLabel = priority => {
  if (priority === 'baixa') return t('POST_SALE.PRIORITY.LOW');
  if (priority === 'alta') return t('POST_SALE.PRIORITY.HIGH');
  if (priority === 'critica') return t('POST_SALE.PRIORITY.CRITICAL');
  return t('POST_SALE.PRIORITY.NORMAL');
};
</script>

<template>
  <section
    class="rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4"
  >
    <p class="text-lg font-semibold text-lais-text">
      {{ caseData.transaction?.title || caseData.transaction?.reference }}
    </p>
    <p class="mt-1 text-sm text-lais-text-muted">
      {{
        caseData.transaction?.address?.endereco ||
        t('POST_SALE.COMMON.ADDRESS_UNAVAILABLE')
      }}
    </p>
  </section>

  <section class="grid gap-3 md:grid-cols-3">
    <LaisCard :title="t('POST_SALE.DETAIL.CURRENT_STATUS')">
      <p class="text-lg font-semibold">{{ caseData.status_label }}</p>
      <p class="mt-2 text-sm text-lais-text-muted">
        {{ formatDate(caseData.status_due_at) }}
      </p>
    </LaisCard>
    <LaisCard :title="t('POST_SALE.DETAIL.RESPONSIBLE')">
      <template v-if="caseData.permissions?.can_assign">
        <select
          :value="responsibleUserId"
          class="w-full rounded-lais border border-lais-border bg-white p-2 text-sm"
          @change="emit('update:responsibleUserId', $event.target.value)"
        >
          <option value="">{{ t('POST_SALE.COMMON.UNASSIGNED') }}</option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
        <button
          type="button"
          class="mt-2 text-sm font-semibold text-lais-primary"
          @click="emit('assign')"
        >
          {{ t('POST_SALE.ACTIONS.ASSIGN') }}
        </button>
      </template>
      <p v-else class="text-sm text-lais-text-body">
        {{ caseData.responsible?.name || t('POST_SALE.COMMON.UNASSIGNED') }}
      </p>
    </LaisCard>
    <LaisCard :title="t('POST_SALE.DETAIL.PRIORITY')">
      <span
        class="inline-flex rounded-full px-2 py-1 text-xs font-semibold"
        :class="priorityClass(caseData.priority)"
      >
        {{ priorityLabel(caseData.priority) }}
      </span>
    </LaisCard>
  </section>
</template>
